import { useEffect, useState } from "react";
import { API_URL } from "@/lib/backend";
import { blocksCart } from "@/lib/agenda";

// Estado da agenda de Natal e da suspensão da loja, vindo do servidor (GET /api/agenda). O servidor volta a validar tudo ao pagar.
let current = null; // null = ainda não carregado (ou sem servidor): a loja não mostra nada natalício
let timer = null;
const listeners = new Set();
const publish = (v) => {
  current = v;
  listeners.forEach((fn) => fn(v));
};

export const refreshAgenda = async () => {
  if (!API_URL) return null;
  try {
    const res = await fetch(`${API_URL}/agenda`, { cache: "no-store" });
    if (!res.ok) return current;
    const data = await res.json();
    // O relógio do servidor serve de referência à contagem decrescente (o do telemóvel pode estar errado).
    publish({ ...data, clockOffset: Date.parse(data.serverNow) - Date.now() });
    return current;
  } catch {
    return current;
  }
};

const onVisible = () => document.visibilityState === "visible" && refreshAgenda();

export const useAgenda = () => {
  const [value, setValue] = useState(current);
  useEffect(() => {
    listeners.add(setValue);
    if (current === null) refreshAgenda();
    else setValue(current);
    if (listeners.size === 1) {
      timer = setInterval(refreshAgenda, 60000);
      document.addEventListener("visibilitychange", onVisible);
    }
    return () => {
      listeners.delete(setValue);
      if (!listeners.size) {
        clearInterval(timer);
        document.removeEventListener("visibilitychange", onVisible);
      }
    };
  }, []);
  return value;
};

/** { agenda, blocked }: `blocked` = a loja não aceita agora encomendas com estas peças (slugs). */
export const useOrderGate = (slugs) => {
  const agenda = useAgenda();
  return { agenda, blocked: blocksCart(agenda, slugs), reason: agenda?.suspended ? "suspended" : "closed" };
};

export const isCovered = (agenda, slugs) => !!agenda && (!agenda.coveredSlugs || slugs.some((s) => agenda.coveredSlugs.includes(s)));

const MONTHS = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
const lisbonParts = (iso) => {
  const p = Object.fromEntries(new Intl.DateTimeFormat("pt-PT", { timeZone: "Europe/Lisbon", day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return { d: Number(p.day), m: Number(p.month) - 1, y: Number(p.year), h: Number(p.hour), min: Number(p.minute) };
};
/** "1 de dezembro" (hora de Lisboa). Com `short`: "1 dez". */
export const dateLabel = (iso, short = false) => {
  const { d, m } = lisbonParts(iso);
  return short ? `${d} ${MONTHS[m].slice(0, 3)}` : `${d} de ${MONTHS[m]}`;
};
/** "23:59" quando a hora não é o fim do dia, para ser clara quando fecha a meio do dia. */
export const timeLabel = (iso) => {
  const { h, min } = lisbonParts(iso);
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
};
export const yearOf = (iso) => lisbonParts(iso).y;
